import React from "react";
import { Box, Grid } from "@mui/material";
import {
  ReceiptLongOutlined, CurrencyRupeeOutlined, PendingActionsOutlined,
  CheckCircleOutlineOutlined, AccountBalanceOutlined, PersonOutlined,
} from "@mui/icons-material";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import DemoShell from "./DemoShell";
import { MetricCard, ChartCard, DarkTooltip } from "./demoComponents";
import { demo } from "./demoStyles";

// ── Mock data ────────────────────────────────────────────────────────────────
const monthlyRevenue = [
  { month: "Dec", collected: 1820000, billed: 2100000 },
  { month: "Jan", collected: 1960000, billed: 2240000 },
  { month: "Feb", collected: 2080000, billed: 2380000 },
  { month: "Mar", collected: 2240000, billed: 2520000 },
  { month: "Apr", collected: 2140000, billed: 2460000 },
  { month: "May", collected: 2380000, billed: 2640000 },
];

const paymentStatus = [
  { name: "Paid",       value: 1086, color: demo.green  },
  { name: "Pending",    value: 124,  color: demo.amber  },
  { name: "Overdue",    value: 38,   color: "#f87171"   },
];

const overduetrend = [
  { week: "W1 Mar", amount: 520000 },
  { week: "W2 Mar", amount: 490000 },
  { week: "W3 Mar", amount: 460000 },
  { week: "W4 Mar", amount: 510000 },
  { week: "W1 Apr", amount: 430000 },
  { week: "W2 Apr", amount: 380000 },
  { week: "W3 Apr", amount: 320000 },
  { week: "W4 Apr", amount: 295000 },
  { week: "W1 May", amount: 310000 },
  { week: "W2 May", amount: 280000 },
];

const recentInvoices = [
  { id: "INV-2024-4821", consumer: "Ramesh Kumar",      amount: "₹4,240",  status: "Paid",    date: "12 May" },
  { id: "INV-2024-4820", consumer: "Sunita Devi",       amount: "₹3,180",  status: "Paid",    date: "11 May" },
  { id: "INV-2024-4819", consumer: "Rajesh Singh",      amount: "₹5,620",  status: "Pending", date: "10 May" },
  { id: "INV-2024-4818", consumer: "Meena Patel",       amount: "₹2,860",  status: "Overdue", date: "28 Apr" },
  { id: "INV-2024-4817", consumer: "Harjeet Kaur",      amount: "₹4,100",  status: "Paid",    date: "28 Apr" },
];

const GRADIENT = "linear-gradient(135deg, #a78bfa, #6366f1)";

const statusColor = { Paid: demo.green, Pending: demo.amber, Overdue: "#f87171" };

export default function BillingPage() {
  return (
    <DemoShell title="Billing & Payments" icon={ReceiptLongOutlined} gradient={GRADIENT}>
      {/* ── KPI row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { icon: CurrencyRupeeOutlined,         label: "Total Revenue (May)",   value: "₹26.4L",  trend: "+11% vs Apr", trendUp: true,  gradient: GRADIENT },
          { icon: CheckCircleOutlineOutlined,    label: "Collected",             value: "₹22.9L",  sub: "86.7% collection rate",          gradient: GRADIENT },
          { icon: PendingActionsOutlined,        label: "Pending",               value: "₹3.5L",   trend: "-8% vs Apr", trendUp: true,   gradient: GRADIENT },
          { icon: ReceiptLongOutlined,           label: "Invoices Raised",       value: "1,248",   sub: "This month",                     gradient: GRADIENT },
          { icon: AccountBalanceOutlined,        label: "Subsidy Claimed",       value: "₹8.4L",   trend: "+4% vs Apr", trendUp: true,   gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
          { icon: PersonOutlined,                label: "Active Consumers",      value: "1,086",   sub: "+24 new this month",             gradient: "linear-gradient(135deg, #34d399, #22d3ee)" },
        ].map((m, i) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
            <MetricCard {...m} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts row 1 ── */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Monthly revenue bar */}
        <Grid item xs={12} lg={8}>
          <ChartCard title="Monthly Billed vs Collected (₹)" subtitle="Last 6 months">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyRevenue} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={6}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: demo.textFaint, fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v/100000).toFixed(1)}L`} />
                <Tooltip content={({ active, payload, label }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "10px", px: 1.8, py: 1.2 }}>
                        <Box sx={{ color: demo.textDim, fontSize: 11.5, mb: 0.8, fontWeight: 600 }}>{label}</Box>
                        {payload.map((p, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: i < payload.length - 1 ? 0.5 : 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
                            <Box sx={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>
                              ₹{(p.value/100000).toFixed(2)}L
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    : null
                } />
                <Legend formatter={v => <span style={{ color: demo.textDim, fontSize: 12 }}>{v === "collected" ? "Collected" : "Billed"}</span>} />
                <Bar dataKey="collected" fill="#a78bfa" radius={[5,5,0,0]} maxBarSize={36} />
                <Bar dataKey="billed"    fill="rgba(167,139,250,0.22)" radius={[5,5,0,0]} maxBarSize={36} stroke="#a78bfa" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Payment status pie */}
        <Grid item xs={12} sm={6} lg={4}>
          <ChartCard title="Payment Status" subtitle="Invoice distribution · This month">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={paymentStatus} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                  paddingAngle={3} dataKey="value" label={({ name, percent }) => `${(percent*100).toFixed(0)}%`}
                  labelLine={false}>
                  {paymentStatus.map((e, i) => <Cell key={i} fill={e.color} stroke="transparent" />)}
                </Pie>
                <Tooltip content={({ active, payload }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "8px", px: 1.6, py: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: payload[0].payload.color }} />
                          <span style={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>{payload[0].payload.name}: {payload[0].value}</span>
                        </Box>
                      </Box>
                    : null
                } />
                <Legend formatter={(v, e) => <span style={{ color: demo.textDim, fontSize: 12 }}>{e.payload.name} ({e.payload.value})</span>} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Charts row 2 ── */}
      <Grid container spacing={2.5}>
        {/* Overdue trend */}
        <Grid item xs={12} lg={5}>
          <ChartCard title="Overdue Amount Trend (₹)" subtitle="Weekly · Last 10 weeks">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={overduetrend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="overdueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#f87171" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f87171" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fill: demo.textFaint, fontSize: 9 }} tickLine={false} interval={2} />
                <YAxis tick={{ fill: demo.textFaint, fontSize: 9 }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v/100000).toFixed(1)}L`} />
                <Tooltip content={({ active, payload, label }) =>
                  active && payload?.length
                    ? <Box sx={{ background: "rgba(12,17,28,0.96)", border: `1px solid ${demo.border}`, borderRadius: "8px", px: 1.6, py: 1 }}>
                        <Box sx={{ color: demo.textDim, fontSize: 11, mb: 0.5 }}>{label}</Box>
                        <Box sx={{ color: "#f87171", fontSize: 13, fontWeight: 700 }}>₹{(payload[0].value/100000).toFixed(2)}L overdue</Box>
                      </Box>
                    : null
                } />
                <Line type="monotone" dataKey="amount" stroke="#f87171" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Recent invoices */}
        <Grid item xs={12} lg={7}>
          <ChartCard title="Recent Invoices" subtitle="Latest 5 transactions">
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr 80px 65px 55px", gap: "0 12px",
                pb: 1, mb: 0.5, borderBottom: `1px solid ${demo.border}` }}>
                {["Invoice", "Consumer", "Amount", "Status", "Date"].map(h => (
                  <Box key={h} sx={{ color: demo.textFaint, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</Box>
                ))}
              </Box>
              {recentInvoices.map((inv, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: "110px 1fr 80px 65px 55px", gap: "0 12px",
                  py: 1.3, borderBottom: `1px solid ${demo.border}`, "&:last-child": { borderBottom: "none" } }}>
                  <Box sx={{ color: demo.accent, fontSize: 12, fontWeight: 700 }}>{inv.id}</Box>
                  <Box sx={{ color: demo.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.consumer}</Box>
                  <Box sx={{ color: demo.text, fontSize: 13, fontWeight: 700 }}>{inv.amount}</Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: statusColor[inv.status] }} />
                    <Box sx={{ color: statusColor[inv.status], fontSize: 12, fontWeight: 600 }}>{inv.status}</Box>
                  </Box>
                  <Box sx={{ color: demo.textDim, fontSize: 12 }}>{inv.date}</Box>
                </Box>
              ))}
            </Box>
          </ChartCard>
        </Grid>
      </Grid>
    </DemoShell>
  );
}
